import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, List, ListItem, ListItemButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useBlocker } from 'react-router-dom';
import BreadCrumbs from '../BreadCrumbs';
import Loading from '../Loading';
import APIUtils from '../../utils/APIUtils';
import { stableSort, getComparator } from '../../uiComponents/MuiTable/MuiHelper';
import GeneralSettingsTab from './GeneralSettingsTab';
import MolecularSettingsTab from './MolecularSettingsTab';
import BiochemicalSettingsTab from './BiochemicalSettingsTab';
import AuditTab from './AuditTab';
import ProgramSettingsDialogs from './ProgramSettingsDialogs';
import useProgramSettingsData from './useProgramSettingsData';
import useCutoffEditor from './useCutoffEditor';
import useCutoffImport from './useCutoffImport';
import useCutoffDelete from './useCutoffDelete';
import useMolecularSettings from './useMolecularSettings';
import useRestrictedConditions from './useRestrictedConditions';
import useSnackbar from './useSnackbar';
import { getConditionKey, parseAnalyteCodeString, stripPendingSaveFlags } from './cutoffUtils';
import './index.css';

const tabs = [
  { key: 'general', label: 'General Program Settings' },
  { key: 'molecular', label: 'Molecular Module Settings' },
  { key: 'biochemical', label: 'Biochemical Module Settings' },
  { key: 'clinical', label: 'Clinical Module Settings' },
  { key: 'audit', label: 'Audit' },
];

const CUTOFF_AUTO_OPTION = 'Use Automatically Calculated ED3N Cutoffs';
const CUTOFF_MANUAL_OPTION = 'Use Manually Specified Cutoffs';

const cutoffTableHeadCells = [
  { label: 'Condition', id: 'conditionSortValue', numeric: false, disablePadding: false },
  { label: 'Primary Analytes', id: 'primaryAnalyte', numeric: false, disablePadding: false },
  { label: 'Secondary Analytes', id: 'secondaryAnalyte', numeric: false, disablePadding: false, type: 'noSort' },
  { label: 'Cutoffs Specified', id: 'cutoffCount', numeric: false, disablePadding: false },
  { label: 'Actions', id: 'actions', numeric: false, disablePadding: false, type: 'blank' },
];

// Total number of individual cutoffs (not conditions) across a set of condition rows.
const countManualCutoffs = (values) =>
  (Array.isArray(values) ? values : []).reduce((sum, condition) => {
    const profile = condition.conditionProfile || condition.ConditionProfile;
    return sum + (Array.isArray(profile) ? profile.length : 0);
  }, 0);

const ProgramSettings = () => {
  const [selectedTab, setSelectedTab] = useState('general');
  const [programId, setProgramId] = useState('');
  const [programShortName, setProgramShortName] = useState('');
  const [breadCrumbs] = useState([
    { name: 'ProgramDashboard' },
    { name: 'ProgramSettings' },
  ]);
  const [loading, setLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);

  // Per-tab error messages (success feedback is handled by the shared snackbar)
  const [biochemicalError, setBiochemicalError] = useState('');

  // Biochemical settings state
  const [biochemicalInstrumentation, setBiochemicalInstrumentation] = useState([]);
  const [selectedBiochemicalValues, setSelectedBiochemicalValues] = useState({});
  const [initialBiochemicalValues, setInitialBiochemicalValues] = useState({});
  const [cutoffSettingsSelection, setCutoffSettingsSelection] = useState(CUTOFF_AUTO_OPTION);
  const [initialCutoffSettingsSelection, setInitialCutoffSettingsSelection] = useState(CUTOFF_AUTO_OPTION);
  const [cutoffValues, setCutoffValues] = useState([]);
  const [initialCutoffValues, setInitialCutoffValues] = useState([]);
  const [defaultCutoffValues, setDefaultCutoffValues] = useState([]);
  const [tempConditionRows, setTempConditionRows] = useState([]);
  const [addConditionDialogOpen, setAddConditionDialogOpen] = useState(false);
  const [cutoffValuesLoaded, setCutoffValuesLoaded] = useState(false);
  const [cutoffLoading, setCutoffLoading] = useState(false);
  const [expandedCutoffRows, setExpandedCutoffRows] = useState({});
  const [modifyPrimaryAnalyteDialogOpen, setModifyPrimaryAnalyteDialogOpen] = useState(false);
  const [modifyPrimaryAnalyteTarget, setModifyPrimaryAnalyteTarget] = useState(null);
  const [modifyPrimaryAnalyteSaving] = useState(false);
  const [cutoffTableOrder, setCutoffTableOrder] = useState('asc');
  const [cutoffTableOrderBy, setCutoffTableOrderBy] = useState('conditionSortValue');

  const [pageLoadError, setPageLoadError] = useState('');
  const [conditionNameByAcronym, setConditionNameByAcronym] = useState({});
  const [analyteDisplayByCode, setAnalyteDisplayByCode] = useState({});
  const [analyteDisplayByShortName, setAnalyteDisplayByShortName] = useState({});
  const [analyteLookupOptions, setAnalyteLookupOptions] = useState([]);

  // Per-tab save state
  const [biochemicalSaving, setBiochemicalSaving] = useState(false);

  const {
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    showSnackbar,
    closeSnackbar,
  } = useSnackbar();

  const {
    CUTOFF_DIALOG_MODE_ADD,
    CUTOFF_DIALOG_MODE_MODIFY,
    cutoffDialogOpen,
    cutoffDialogValues,
    cutoffDialogErrors,
    cutoffDialogSaving,
    openCutoffDialog,
    closeCutoffDialog,
    handleCutoffDialogValueChange,
    handleSaveCutoffDialog,
    getCutoffDialogTitle,
  } = useCutoffEditor({
    cutoffValues,
    setCutoffValues,
    setBiochemicalError,
    tempConditionRows,
    setTempConditionRows,
  });

  const {
    disabledConditions,
    enabledConditions,
    initialDisabledConditions,
    initialEnabledConditions,
    selectedDisabled,
    selectedEnabled,
    conditionsLoading,
    conditionsChecking,
    conditionsDirty,
    conditionsError,
    cutoffWarningDialogOpen,
    cutoffWarningConditions,
    setDisabledConditions,
    setEnabledConditions,
    setInitialDisabledConditions,
    setInitialEnabledConditions,
    setConditionsDirty,
    setConditionsError,
    setConditionsLoading,
    handleToggleDisabledSelection,
    handleToggleEnabledSelection,
    handleMoveToEnabled,
    handleMoveToDisabled,
    handleCancelConditions,
    handleApplyConditions,
    handleConfirmCutoffWarning,
    handleCancelCutoffWarning,
  } = useRestrictedConditions({
    programId,
    setCutoffValues,
    setInitialCutoffValues,
    showSnackbar,
  });

  const {
    importCutoffsDialogOpen,
    importCutoffsImporting,
    importCutoffsValidatedOpen,
    importCutoffsNoChangesOpen,
    importCutoffsErrorOpen,
    importCutoffsErrorMessages,
    pendingImportConditions,
    importedCutoffCount,
    setImportCutoffsValidatedOpen,
    setImportCutoffsNoChangesOpen,
    setImportCutoffsErrorOpen,
    setPendingImportConditions,
    setImportedCutoffCount,
    handleOpenImportCutoffsDialog,
    handleCloseImportCutoffsDialog,
    handleImportCutoffs,
  } = useCutoffImport({
    programId,
    cutoffValues,
    enabledConditions,
    setCutoffValues,
    setTempConditionRows,
    setExpandedCutoffRows,
    setBiochemicalError,
  });

  const {
    cutoffDeleteSaving,
    cutoffDeleteConfirmOpen,
    handleRequestDeleteCutoff,
    handleCancelDeleteCutoff,
    handleConfirmDeleteCutoff,
    deleteAllCutoffsConfirmOpen,
    handleRequestDeleteAllCutoffs,
    handleCancelDeleteAllCutoffs,
    handleConfirmDeleteAllCutoffs,
  } = useCutoffDelete({
    cutoffValues,
    setCutoffValues,
    setTempConditionRows,
    setBiochemicalError,
  });

  const {
    defaultInterpretationCount,
    reviewThreshold,
    defaultHgvsRef,
    defaultInterpreters,
    availableUsers,
    molecularError,
    validationErrors,
    molecularSaving,
    molecularDirty,
    setReviewThreshold,
    setDefaultHgvsRef,
    setInitialReviewThreshold,
    setInitialDefaultHgvsRef,
    setInitialInterpretationCount,
    setDefaultInterpretationCount,
    setInitialInterpreters,
    setDefaultInterpreters,
    setAvailableUsers,
    setInputValues,
    setMolecularError,
    handleInterpretationCountChange,
    handleAddInterpreter,
    handleCancelMolecular,
    onSubmitMolecular,
    renderComboboxes,
  } = useMolecularSettings({
    programId,
    biochemicalInstrumentation,
    selectedBiochemicalValues,
    cutoffSettingsSelection,
    CUTOFF_MANUAL_OPTION,
    showSnackbar,
  });

  // Unsaved changes dialog state (for tab switching)
  const [pendingTab, setPendingTab] = useState(null);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);

  const ed3nConfig = __ED3N_CONFIG__;

  // --- Reusable disabled info icon ---
  const infoIconStyle = {
    fontSize: '1.1rem',
    color: 'var(--info-color)',
    cursor: 'pointer',
    pointerEvents: 'auto',
    marginLeft: '4px',
    marginRight: '8px',
    verticalAlign: 'middle',
  };

  const tooltipProps = {
    placement: 'top',
    arrow: true,
    componentsProps: {
      tooltip: {
        sx: {
          fontSize: '14px',
        },
      },
    },
  };

  const stickyActionBarSx = {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    mt: 2,
    position: 'sticky',
    bottom: 0,
    backgroundColor: '#fff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    pt: 1,
    pb: 1,
    mb: 0.5,
    zIndex: 2,
  };

  const renderDisabledInfoIcon = (message) => (
    <Tooltip title={message} {...tooltipProps}>
      <span tabIndex={0} role="button" aria-label={message}>
        <InfoOutlined style={infoIconStyle} />
      </span>
    </Tooltip>
  );

  // --- Dirty tracking computed values ---
  const biochemicalDirty = useMemo(() => {
    const shouldTrackCutoffValueChanges =
      cutoffSettingsSelection === CUTOFF_MANUAL_OPTION
      || initialCutoffSettingsSelection === CUTOFF_MANUAL_OPTION;

    // Any temp condition row (even empty) counts as an unsaved change since it will be lost on reload
    const hasTempRows = tempConditionRows.length > 0;

    return JSON.stringify(selectedBiochemicalValues) !== JSON.stringify(initialBiochemicalValues)
      || (shouldTrackCutoffValueChanges && JSON.stringify(cutoffValues) !== JSON.stringify(initialCutoffValues))
      || cutoffSettingsSelection !== initialCutoffSettingsSelection
      || hasTempRows;
  }, [
    selectedBiochemicalValues,
    initialBiochemicalValues,
    cutoffValues,
    initialCutoffValues,
    cutoffSettingsSelection,
    initialCutoffSettingsSelection,
    tempConditionRows,
  ]);

  const anyTabDirty = conditionsDirty || molecularDirty || biochemicalDirty;

  useEffect(() => {
    const handler = (e) => {
      if (anyTabDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anyTabDirty]);

  const blocker = useBlocker(useMemo(() => anyTabDirty, [anyTabDirty]));

  // Release the router-level blocker once the tabs are no longer dirty, so an
  // intentional navigation evaluated mid-transition can't leave the blocker
  // stuck in the "blocked" state and silently swallow later navigations.
  useEffect(() => {
    if (blocker.state === "blocked" && !anyTabDirty) {
      blocker.proceed();
    }
  }, [blocker, anyTabDirty]);

  // Safety net: release the blocker if this page unmounts while still blocked.
  useEffect(() => {
    return () => {
      if (blocker.state === "blocked") {
        blocker.reset();
      }
    };
  }, [blocker]);

  const handleAcceptNavigation = () => {
    blocker.proceed?.();
  };
  const handleCancelNavigation = () => {
    blocker.reset?.();
  };

  const handleTabChange = (tabKey) => {
    if (tabKey === selectedTab) return;
    const currentTabDirty =
      (selectedTab === 'general' && conditionsDirty) ||
      (selectedTab === 'molecular' && molecularDirty) ||
      (selectedTab === 'biochemical' && biochemicalDirty);
    if (currentTabDirty) {
      setPendingTab(tabKey);
      setUnsavedDialogOpen(true);
    } else {
      setSelectedTab(tabKey);
    }
  };

  const handleUnsavedDialogYes = () => {
    setUnsavedDialogOpen(false);
    if (selectedTab === 'biochemical') {
      handleCancelBiochemical();
    }
    setSelectedTab(pendingTab);
    setPendingTab(null);
  };

  const handleUnsavedDialogNo = () => {
    setUnsavedDialogOpen(false);
    setPendingTab(null);
  };

  const getConditionDisplayName = useCallback((conditionAcronym) => {
    const name = conditionNameByAcronym[conditionAcronym];
    return name ? `${conditionAcronym} - ${name}` : conditionAcronym;
  }, [conditionNameByAcronym]);

  const getCutoffConditionDisplayName = useCallback((condition) => {
    if (!condition) return '';
    const acronym = condition.acronym || condition.Acronym;
    const name = condition.name || condition.Name;
    if (acronym) {
      return name ? `${acronym} - ${name}` : acronym;
    }
    return name || '';
  }, []);

  const handleCutoffTableRequestSort = useCallback((event, property) => {
    const isAsc = cutoffTableOrderBy === property && cutoffTableOrder === 'asc';
    setCutoffTableOrder(isAsc ? 'desc' : 'asc');
    setCutoffTableOrderBy(property);
  }, [cutoffTableOrder, cutoffTableOrderBy]);

  // Include temp condition rows (empty conditions added via Add Condition dialog) in the manual table,
  // merged and sorted together with saved rows so temp rows appear in the correct sort position.
  const sortedManualCutoffValuesWithTemp = useMemo(() => {
    const savedRows = Array.isArray(cutoffValues)
      ? cutoffValues
          .filter((condition) => {
            const profile = condition.conditionProfile || condition.ConditionProfile;
            return Array.isArray(profile) && profile.length > 0 && condition.enabled !== false;
          })
          .map((condition) => ({
            ...condition,
            conditionSortValue: getCutoffConditionDisplayName(condition),
            cutoffCount: (condition.conditionProfile || condition.ConditionProfile).length,
            isTempRow: false,
          }))
      : [];

    const tempRows = tempConditionRows
      .filter((tc) => {
        // Don't include temp rows if condition already has profile data in cutoffValues
        const existsInCutoffs = cutoffValues.some(
          (cv) => getConditionKey(cv) === getConditionKey(tc) &&
            Array.isArray(cv.conditionProfile || cv.ConditionProfile) &&
            (cv.conditionProfile || cv.ConditionProfile).length > 0
        );
        return !existsInCutoffs;
      })
      .map((condition) => ({
        ...condition,
        conditionSortValue: getCutoffConditionDisplayName(condition),
        cutoffCount: 0,
        isTempRow: true,
      }));

    return stableSort([...savedRows, ...tempRows], getComparator(cutoffTableOrder, cutoffTableOrderBy));
  }, [cutoffValues, tempConditionRows, cutoffTableOrder, cutoffTableOrderBy, getCutoffConditionDisplayName]);

  // Whether there are any manual cutoffs available to delete. This intentionally
  // does NOT filter out disabled conditions (enabled === false), because a
  // disabled condition still retains its persisted manual cutoffs. If we based
  // this on the enabled-filtered table rows, the "Delete All Cutoffs" button
  // would disappear when every remaining cutoff belongs to a disabled condition,
  // making those cutoffs impossible to clear.
  const hasManualCutoffs = useMemo(() => {
    const hasSavedCutoffs = Array.isArray(cutoffValues) && cutoffValues.some((c) => {
      const profile = c.conditionProfile || c.ConditionProfile;
      return Array.isArray(profile) && profile.length > 0;
    });
    return hasSavedCutoffs || tempConditionRows.length > 0;
  }, [cutoffValues, tempConditionRows]);

  // Total individual cutoffs staged for deletion by "Delete All Cutoffs".
  const totalManualCutoffCount = useMemo(
    () => countManualCutoffs(cutoffValues) + countManualCutoffs(tempConditionRows),
    [cutoffValues, tempConditionRows],
  );

  // Static default cutoff table (only conditions with conditionProfile data)
  const sortedDefaultCutoffValues = useMemo(() => {
    const cutoffRows = Array.isArray(defaultCutoffValues)
      ? defaultCutoffValues
          .filter((condition) => {
            const profile = condition.conditionProfile || condition.ConditionProfile;
            return Array.isArray(profile) && profile.length > 0;
          })
          .map((condition) => ({
            ...condition,
            conditionSortValue: getCutoffConditionDisplayName(condition),
            cutoffCount: (condition.conditionProfile || condition.ConditionProfile).length,
          }))
      : [];

    return stableSort(cutoffRows, getComparator(cutoffTableOrder, cutoffTableOrderBy));
  }, [defaultCutoffValues, cutoffTableOrder, cutoffTableOrderBy, getCutoffConditionDisplayName]);

  // Available conditions for "Add Condition" dialog
  // Conditions from defaultCutOff that have NO conditionProfile data (not currently shown),
  // and also not already in the manual cutoff table or temp rows
  const availableConditionsForAdd = useMemo(() => {
    // Build a lookup map from defaultCutoffValues by key for metadata enrichment.
    // Index by BOTH the primary key (may be orphanetCode) AND the acronym so that
    // the acronym-based lookup below succeeds regardless of what getConditionKey returns.
    // Strip both profile fields so the default cutoff data is never carried into the
    // manual add flow as a stale payload field.
    const defaultCutoffByKey = new Map();
    (Array.isArray(defaultCutoffValues) ? defaultCutoffValues : []).forEach((c) => {
      // eslint-disable-next-line no-unused-vars
      const { conditionProfile: _cp, ConditionProfile: _CP, ...conditionMetadata } = c;
      defaultCutoffByKey.set(getConditionKey(c), conditionMetadata);
      if (c.acronym) defaultCutoffByKey.set(c.acronym, conditionMetadata);
      if (c.Acronym) defaultCutoffByKey.set(c.Acronym, conditionMetadata);
    });

    // Exclude only conditions that are ACTUALLY displayed in the manual cutoff table,
    // i.e. those with a non-empty conditionProfile. Conditions present in cutoffValues
    // with an empty profile are not shown in the table, so they must remain available
    // to add (otherwise valid enabled conditions silently disappear from the dialog).
    // Index by both the primary key (may be orphanetCode) AND acronym so that the
    // acronym-based enabledConditions lookup matches correctly regardless of key type.
    const manualConditionKeys = new Set();
    (Array.isArray(cutoffValues) ? cutoffValues : []).forEach((c) => {
      const profile = c.conditionProfile || c.ConditionProfile;
      const hasProfileData = Array.isArray(profile) && profile.length > 0;
      if (!hasProfileData) return;
      manualConditionKeys.add(getConditionKey(c));
      if (c.acronym) manualConditionKeys.add(c.acronym);
      if (c.Acronym) manualConditionKeys.add(c.Acronym);
    });

    // Exclude conditions already in temp rows
    const tempKeys = new Set();
    tempConditionRows.forEach((tc) => {
      tempKeys.add(getConditionKey(tc));
      if (tc.acronym) tempKeys.add(tc.acronym);
      if (tc.Acronym) tempKeys.add(tc.Acronym);
    });

    // Use enabledConditions as the source of truth — only program-allowed conditions appear here.
    // For each enabled acronym, use the full object from defaultCutoffValues if available
    // (for metadata like orphanetCode, name, etc.), otherwise build a minimal object.
    return enabledConditions
      .filter((acronym) => {
        if (manualConditionKeys.has(acronym) || tempKeys.has(acronym)) return false;
        return true;
      })
      .map((acronym) => {
        return defaultCutoffByKey.get(acronym) || {
          acronym,
          name: conditionNameByAcronym[acronym] || acronym,
        };
      });
  }, [defaultCutoffValues, cutoffValues, tempConditionRows, enabledConditions, conditionNameByAcronym]);

  const getAnalyteDisplayName = useCallback((analyteValue) => {
    if (!analyteValue) return '';
    return analyteDisplayByCode[analyteValue]
      || analyteDisplayByShortName[analyteValue]
      || analyteValue;
  }, [analyteDisplayByCode, analyteDisplayByShortName]);

  const { loadCutoffValues } = useProgramSettingsData({
    CUTOFF_AUTO_OPTION,
    CUTOFF_MANUAL_OPTION,
    setProgramId,
    setLoading,
    setDataReady,
    setPageLoadError,
    setConditionNameByAcronym,
    setAnalyteDisplayByCode,
    setAnalyteDisplayByShortName,
    setAnalyteLookupOptions,
    setBiochemicalInstrumentation,
    setSelectedBiochemicalValues,
    setInitialBiochemicalValues,
    setCutoffSettingsSelection,
    setInitialCutoffSettingsSelection,
    setCutoffValues,
    setInitialCutoffValues,
    setDefaultCutoffValues,
    setCutoffValuesLoaded,
    setCutoffLoading,
    setExpandedCutoffRows,
    setBiochemicalError,
    setDisabledConditions,
    setEnabledConditions,
    setInitialDisabledConditions,
    setInitialEnabledConditions,
    setConditionsDirty,
    setConditionsError,
    setConditionsLoading,
    setInputValues,
    setProgramShortName,
    setReviewThreshold,
    setInitialReviewThreshold,
    setDefaultHgvsRef,
    setInitialDefaultHgvsRef,
    setInitialInterpretationCount,
    setDefaultInterpretationCount,
    setInitialInterpreters,
    setDefaultInterpreters,
    setAvailableUsers,
    setTempConditionRows,
  });

  // --- Biochemical handlers ---
  const handleBiochemicalTableSelectionChange = (analyteGroup, values) => {
    setSelectedBiochemicalValues(prev => ({
      ...prev,
      [analyteGroup]: { ...prev[analyteGroup], ...values },
    }));
  };

  const handleToggleCutoffRow = (rowKey) => {
    setExpandedCutoffRows(prev => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const handleCutoffSettingsSelectionChange = async (e) => {
    const nextSelection = e.target.value;
    setCutoffSettingsSelection(nextSelection);
    setExpandedCutoffRows({});
    // Clear any temporary condition rows when switching modes — they are not persisted
    setTempConditionRows([]);

    if (nextSelection === CUTOFF_MANUAL_OPTION && !cutoffValuesLoaded) {
      await loadCutoffValues(programId);
    }
  };

  // --- Add Condition dialog handlers ---
  const handleOpenAddConditionDialog = () => {
    setAddConditionDialogOpen(true);
  };

  const handleCloseAddConditionDialog = () => {
    setAddConditionDialogOpen(false);
  };

  const handleAddConditionFromDialog = (condition) => {
    setTempConditionRows((prev) => [...prev, { ...condition, conditionProfile: [], enabled: true }]);
    setAddConditionDialogOpen(false);
  };

  const handleOpenModifyPrimaryAnalyteDialog = (condition) => {
    setModifyPrimaryAnalyteTarget(condition);
    setModifyPrimaryAnalyteDialogOpen(true);
  };

  const handleCloseModifyPrimaryAnalyteDialog = () => {
    setModifyPrimaryAnalyteDialogOpen(false);
    setModifyPrimaryAnalyteTarget(null);
  };

  const handleSaveModifyPrimaryAnalyte = (condition, primaryAnalyte) => {
    const conditionKey = getConditionKey(condition);
    const isTempCondition = !cutoffValues.some(c => getConditionKey(c) === conditionKey);

    if (isTempCondition) {
      // Temp conditions have no conditionProfile yet, so no priority recalc needed
      setTempConditionRows(prev => prev.map(tc =>
        getConditionKey(tc) === conditionKey ? { ...tc, primaryAnalyte } : tc
      ));
      setModifyPrimaryAnalyteDialogOpen(false);
      setModifyPrimaryAnalyteTarget(null);
      return;
    }

    // Recalculate priority on every existing profile entry for this condition,
    // because a primary analyte change can flip profiles between priority 1 and 2.
    const primaryCodes = parseAnalyteCodeString(primaryAnalyte || '');
    const recalcPriority = (profile) => {
      if (profile.cutoffType === 'value' || profile.cutoffType === 'SINGLE_ANALYTE') {
        // Single-analyte: check if the analyte code is among the (comma-separated) primary analytes
        return profile.analyteCode1 && primaryCodes.includes(profile.analyteCode1) ? 1 : 2;
      }
      // Ratio / sum: check intersection of all involved codes with the new primary codes
      const numeratorCodes = Array.isArray(profile.ratioNumeratorAnalyteCodes) && profile.ratioNumeratorAnalyteCodes.length
        ? profile.ratioNumeratorAnalyteCodes
        : parseAnalyteCodeString(profile.analyteCode1 || '');
      const denominatorCodes = Array.isArray(profile.ratioDenominatorAnalyteCodes) && profile.ratioDenominatorAnalyteCodes.length
        ? profile.ratioDenominatorAnalyteCodes
        : parseAnalyteCodeString(profile.analyteCode2 || '');
      const sumCodes = Array.isArray(profile.analyteCodes) && profile.analyteCodes.length
        ? profile.analyteCodes
        : parseAnalyteCodeString(profile.analyteCode1 || '');
      const allCodes = profile.cutoffType === 'sum'
        ? sumCodes
        : [...new Set([...numeratorCodes, ...denominatorCodes])];
      return primaryCodes.length > 0 && allCodes.some(code => primaryCodes.includes(code)) ? 1 : 2;
    };

    const nextCutoffValues = cutoffValues.map(c => {
      if (getConditionKey(c) !== conditionKey) return c;
      const profiles = Array.isArray(c.conditionProfile || c.ConditionProfile)
        ? (c.conditionProfile || c.ConditionProfile)
        : [];
      const updatedProfiles = profiles.map(p => ({ ...p, priority: recalcPriority(p) }));
      return { ...c, primaryAnalyte, conditionProfile: updatedProfiles };
    });

    // Stage only — Apply is the single source of truth for saving cutoffs.
    // initialCutoffValues is left unchanged so the tab stays dirty until Apply.
    setBiochemicalError('');
    setCutoffValues(nextCutoffValues);
    setModifyPrimaryAnalyteDialogOpen(false);
    setModifyPrimaryAnalyteTarget(null);
  };

  // --- Cancel/Revert Biochemical Settings ---
  const handleCancelBiochemical = () => {
    setSelectedBiochemicalValues(JSON.parse(JSON.stringify(initialBiochemicalValues)));
    setCutoffValues(JSON.parse(JSON.stringify(initialCutoffValues)));
    setCutoffSettingsSelection(initialCutoffSettingsSelection);
    setExpandedCutoffRows({});
    setTempConditionRows([]);
    setPendingImportConditions([]);
    setImportedCutoffCount(0);
    setBiochemicalError('');
  };

  // --- Save Biochemical Settings ---
  const onSubmitBiochemical = async () => {
    const mergedBiochemicalInstrumentation = biochemicalInstrumentation.map(row => ({
      ...row,
      ...(selectedBiochemicalValues[row.AnalyteGroup] || {}),
    }));

    // Include molecular data to avoid nulling it out
    const cleanedUsers = defaultInterpreters.filter(user => user && user.trim() !== '' && user !== '__new__');
    const visibleUser = cleanedUsers.slice(0, defaultInterpretationCount);

    const payload = {
      reviewThreshold,
      defaultInterpretationCount,
      defaultInterpreters: visibleUser,
      defaultHgvsRef,
      biochemicalInstrumentationSettings: mergedBiochemicalInstrumentation,
      hasManualCutoff: cutoffSettingsSelection === CUTOFF_MANUAL_OPTION,
    };

    try {
      setBiochemicalSaving(true);
      setBiochemicalError('');
      await APIUtils.updateProgramSettings(programId, payload);
      let savedCutoffValues = cutoffValues;
      if (cutoffSettingsSelection === CUTOFF_MANUAL_OPTION && cutoffValuesLoaded) {
        // Merge temp rows that have cutoff data into the save payload
        const tempRowsWithData = tempConditionRows.filter((tc) => {
          const profile = tc.conditionProfile || tc.ConditionProfile;
          return Array.isArray(profile) && profile.length > 0;
        });
        // Strip the UI-only isPendingSave highlight flag so it is never
        // persisted and the highlights clear once the save succeeds.
        savedCutoffValues = stripPendingSaveFlags([...cutoffValues, ...tempRowsWithData]);
        await APIUtils.updateCutoffValues(programId, savedCutoffValues);
        setCutoffValues(savedCutoffValues);
        // Only sync the cutoff baseline when cutoffs were actually persisted.
        // Otherwise staged manual edits would be marked "saved" locally and
        // switching back to Manual could disable Apply even though the server
        // never received them.
        setInitialCutoffValues(JSON.parse(JSON.stringify(savedCutoffValues)));
      }
      // Update initial state locally — no need to reload since the table already shows the saved data
      setInitialBiochemicalValues(JSON.parse(JSON.stringify(selectedBiochemicalValues)));
      setInitialCutoffSettingsSelection(cutoffSettingsSelection);
      setTempConditionRows([]);
      setPendingImportConditions([]);
      // Report how many individual cutoffs were removed by this save (e.g. Delete All).
      const deletedCutoffCount = countManualCutoffs(initialCutoffValues) - countManualCutoffs(savedCutoffValues);
      const persistedCutoffs = cutoffSettingsSelection === CUTOFF_MANUAL_OPTION && cutoffValuesLoaded;
      let successMessage = 'Biochemical settings were saved successfully.';
      if (persistedCutoffs && importedCutoffCount > 0) {
        successMessage += ` ${importedCutoffCount} analyte ${importedCutoffCount === 1 ? 'cutoff' : 'cutoffs'} imported.`;
      }
      if (deletedCutoffCount > 0) {
        successMessage += ` ${deletedCutoffCount} analyte ${deletedCutoffCount === 1 ? 'cutoff' : 'cutoffs'} deleted.`;
      }
      setImportedCutoffCount(0);
      showSnackbar(successMessage);
    } catch (err) {
      console.error('API error:', err);
      let userMessage = 'An error occurred while saving biochemical settings. Please try again.';
      if (err.response?.data?.message) {
        userMessage = err.response.data.message;
      }
      setBiochemicalError(userMessage);
    } finally {
      setBiochemicalSaving(false);
    }
  };

  // --- Tab content renderers ---
  const renderGeneralSettings = () => (
    <GeneralSettingsTab
      tabProps={{
        programShortName,
        programId,
        tooltipProps,
        infoIconStyle,
        conditionsDirty,
        conditionsError,
        setConditionsError,
        conditionsLoading,
        conditionsChecking,
        disabledConditions,
        enabledConditions,
        selectedDisabled,
        selectedEnabled,
        getConditionDisplayName,
        handleToggleDisabledSelection,
        handleToggleEnabledSelection,
        handleMoveToEnabled,
        handleMoveToDisabled,
        stickyActionBarSx,
        renderDisabledInfoIcon,
        handleCancelConditions,
        handleApplyConditions,
      }}
    />
  );

  const renderMolecularSettings = () => (
    <MolecularSettingsTab
      tabProps={{
        molecularDirty,
        molecularError,
        setMolecularError,
        tooltipProps,
        infoIconStyle,
        defaultInterpretationCount,
        handleInterpretationCountChange,
        reviewThreshold,
        setReviewThreshold,
        defaultHgvsRef,
        setDefaultHgvsRef,
        validationErrors,
        renderComboboxes,
        stickyActionBarSx,
        renderDisabledInfoIcon,
        handleCancelMolecular,
        molecularSaving,
        onSubmitMolecular,
      }}
    />
  );

  const renderBiochemicalSettings = () => (
    <BiochemicalSettingsTab
      tabProps={{
        biochemicalDirty,
        biochemicalError,
        setBiochemicalError,
        ed3nConfig,
        tooltipProps,
        infoIconStyle,
        biochemicalInstrumentation,
        selectedBiochemicalValues,
        handleBiochemicalTableSelectionChange,
        cutoffSettingsSelection,
        handleCutoffSettingsSelectionChange,
        CUTOFF_AUTO_OPTION,
        CUTOFF_MANUAL_OPTION,
        cutoffTableHeadCells,
        cutoffTableOrder,
        cutoffTableOrderBy,
        handleCutoffTableRequestSort,
        cutoffLoading,
        sortedCutoffValues: sortedManualCutoffValuesWithTemp,
        sortedDefaultCutoffValues,
        expandedCutoffRows,
        handleToggleCutoffRow,
        setExpandedCutoffRows,
        getCutoffConditionDisplayName,
        getAnalyteDisplayName,
        CUTOFF_DIALOG_MODE_ADD,
        CUTOFF_DIALOG_MODE_MODIFY,
        openCutoffDialog,
        handleRequestDeleteCutoff,
        handleRequestDeleteAllCutoffs,
        hasManualCutoffs,
        cutoffDeleteSaving,
        stickyActionBarSx,
        renderDisabledInfoIcon,
        handleCancelBiochemical,
        biochemicalSaving,
        onSubmitBiochemical,
        handleOpenAddConditionDialog,
        handleCloseAddConditionDialog,
        handleAddConditionFromDialog,
        addConditionDialogOpen,
        availableConditionsForAdd,
        analyteLookupOptions,
        modifyPrimaryAnalyteDialogOpen,
        modifyPrimaryAnalyteTarget,
        modifyPrimaryAnalyteSaving,
        handleOpenModifyPrimaryAnalyteDialog,
        handleCloseModifyPrimaryAnalyteDialog,
        handleSaveModifyPrimaryAnalyte,
        importCutoffsDialogOpen,
        importCutoffsImporting,
        handleOpenImportCutoffsDialog,
        handleCloseImportCutoffsDialog,
        handleImportCutoffs,
      }}
    />
  );

  const renderClinicalSettings = () => (
    <Box className="psTabContent">
      <Typography variant="h6" component="h2" className="psTabTitle">Clinical Module Settings</Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', mt: 2 }}>
        No settings available at this time.
      </Typography>
    </Box>
  );

  const renderAuditTab = () => (
    <AuditTab programId={programId} />
  );

  const tabContentMap = {
    general: renderGeneralSettings,
    molecular: renderMolecularSettings,
    biochemical: renderBiochemicalSettings,
    clinical: renderClinicalSettings,
    audit: renderAuditTab,
  };

  return (
    <div className="programSettings pageMinHeight">
      <Loading isLoading={loading} />
      <Helmet>
        <title>ED3N - Program Settings</title>
      </Helmet>
      <BreadCrumbs breadCrumbs={breadCrumbs} />
      <h1 className="pageTitle">Program Settings</h1>

      {dataReady && (
        <>
          <Box className="psContainer">
            {/* Left sidebar tabs */}
            <Box className="psSidebar">
              <List disablePadding>
                {tabs.map(tab => {
                  const isActive = selectedTab === tab.key;
                  return (
                    <ListItem
                      key={tab.key}
                      disablePadding
                      className={`psTabItem ${isActive ? 'psTabActive' : 'psTabInactive'}`}
                    >
                      <ListItemButton
                        selected={isActive}
                        onClick={() => handleTabChange(tab.key)}
                        sx={{
                          py: 1.5,
                          '&.Mui-selected': {
                            backgroundColor: 'transparent',
                          },
                          '&.Mui-selected:hover': {
                            backgroundColor: 'transparent',
                          },
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isActive ? 'bold' : 'normal',
                            color: isActive ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.55)',
                          }}
                        >
                          {tab.label}
                        </Typography>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>

            {/* Right content area */}
            <Box className="psContent">
              {tabContentMap[selectedTab]()}
            </Box>
          </Box>
        </>
      )}

      {pageLoadError && !loading && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setPageLoadError('')}>
          {pageLoadError}
        </Alert>
      )}

      <ProgramSettingsDialogs
        dialogProps={{
          cutoffDialogOpen,
          getCutoffDialogTitle,
          cutoffDialogValues,
          cutoffDialogErrors,
          cutoffDialogSaving,
          analyteLookupOptions,
          closeCutoffDialog,
          handleSaveCutoffDialog,
          handleCutoffDialogValueChange,
          unsavedDialogOpen,
          handleUnsavedDialogNo,
          handleUnsavedDialogYes,
          blocker,
          handleCancelNavigation,
          handleAcceptNavigation,
          cutoffDeleteConfirmOpen,
          handleCancelDeleteCutoff,
          handleConfirmDeleteCutoff,
          cutoffDeleteSaving,
          deleteAllCutoffsConfirmOpen,
          handleCancelDeleteAllCutoffs,
          handleConfirmDeleteAllCutoffs,
          deleteAllCutoffCount: totalManualCutoffCount,
          cutoffWarningDialogOpen,
          handleCancelCutoffWarning,
          cutoffWarningConditions,
          getCutoffConditionDisplayName,
          handleConfirmCutoffWarning,
          conditionsLoading,
          importCutoffsValidatedOpen,
          setImportCutoffsValidatedOpen,
          importCutoffsNoChangesOpen,
          setImportCutoffsNoChangesOpen,
          importCutoffsErrorOpen,
          setImportCutoffsErrorOpen,
          importCutoffsErrorMessages,
          pendingImportConditions,
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbarSeverity}
          variant="standard"
          sx={{ width: '100%', boxShadow: 6 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ProgramSettings;
